import { useState, useEffect, useRef } from "react";
import {
  Target,
  X,
  Mail,
  Phone,
  Building,
  Briefcase,
  Loader2,
  Edit,
  MapPin,
  Globe,
  Factory,
  User,
  ChevronDown,
  Search,
  Check,
} from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";

const API = "/api";

// US States list
const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
  { value: "DC", label: "District of Columbia" },
];

// Industries list
const INDUSTRIES = [
  "Agriculture",
  "Automotive",
  "Banking & Financial Services",
  "Biotechnology",
  "Construction",
  "Consulting",
  "Consumer Goods",
  "Education",
  "Energy & Utilities",
  "Entertainment & Media",
  "Food & Beverage",
  "Government",
  "Healthcare",
  "Home Services",
  "Hospitality & Tourism",
  "Information Technology",
  "Insurance",
  "Legal Services",
  "Logistics & Transportation",
  "Manufacturing",
  "Marketing & Advertising",
  "Mining & Metals",
  "Non-Profit",
  "Pharmaceuticals",
  "Professional Services",
  "Real Estate",
  "Retail",
  "Telecommunications",
  "Wholesale & Distribution",
];

// Searchable Select Component
const SearchableSelect = ({ 
  options, 
  value, 
  onChange, 
  placeholder, 
  icon: Icon,
  testId 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  const filteredOptions = options.filter(opt => 
    opt.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-[#121212] border border-white/10 rounded-lg pl-8 pr-8 py-2 text-sm text-left text-white focus:outline-none focus:border-[#14A800]/50 flex items-center justify-between"
        data-testid={testId}
      >
        {Icon && <Icon className="absolute left-2.5 w-3.5 h-3.5 text-zinc-500" />}
        <span className={value ? "text-white" : "text-zinc-600"}>
          {value || placeholder}
        </span>
        <ChevronDown className={`absolute right-2.5 w-4 h-4 text-zinc-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl overflow-hidden">
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full bg-[#121212] border border-white/10 rounded-lg pl-8 pr-2 py-1.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#14A800]/50"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    onChange(opt);
                    setIsOpen(false);
                    setSearch("");
                  }}
                  className={`w-full px-3 py-2 text-sm text-left hover:bg-white/5 flex items-center justify-between ${
                    value === opt ? "bg-[#14A800]/10 text-[#14A800]" : "text-white"
                  }`}
                >
                  {opt}
                  {value === opt && <Check className="w-4 h-4" />}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-zinc-500">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * LeadFormModal - Reusable modal for creating and editing leads
 * Enhanced with Company and Contact information sections
 */
export const LeadFormModal = ({ isOpen, onClose, onSuccess, lead = null, mode = "create" }) => {
  const isEditMode = mode === "edit" && lead;
  
  const [formData, setFormData] = useState({
    // Company Information
    company_name: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state: "",
    zip_code: "",
    country: "USA",
    company_phone: "",
    company_email: "",
    industry: "",
    // Contact Information
    first_name: "",
    last_name: "",
    job_title: "",
    mobile_phone: "",
    email: "",
  });
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("company");
  const token = localStorage.getItem("admin_token");

  // Populate form with lead data when editing
  useEffect(() => {
    if (isEditMode && lead) {
      setFormData({
        company_name: lead.company_name || "",
        address_line_1: lead.address_line_1 || lead.address || "",
        address_line_2: lead.address_line_2 || "",
        city: lead.city || "",
        state: lead.state || "",
        zip_code: lead.zip_code || "",
        country: lead.country || "USA",
        company_phone: lead.company_phone || "",
        company_email: lead.company_email || "",
        industry: lead.industry || "",
        first_name: lead.first_name || "",
        last_name: lead.last_name || "",
        job_title: lead.job_title || "",
        mobile_phone: lead.mobile_phone || "",
        email: lead.email || "",
      });
    } else if (mode === "create") {
      setFormData({
        company_name: "",
        address_line_1: "",
        address_line_2: "",
        city: "",
        state: "",
        zip_code: "",
        country: "USA",
        company_phone: "",
        company_email: "",
        industry: "",
        first_name: "",
        last_name: "",
        job_title: "",
        mobile_phone: "",
        email: "",
      });
      setActiveSection("company");
    }
  }, [isEditMode, lead, mode, isOpen]);

  // Format phone number as (XXX)XXX-XXXX
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) {
      return digits.length > 0 ? `(${digits}` : '';
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)})${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  // Format ZIP code (5 digits or 5+4)
  const formatZipCode = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 5) {
      return digits;
    } else {
      return `${digits.slice(0, 5)}-${digits.slice(5, 9)}`;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'mobile_phone' || name === 'company_phone') {
      setFormData(prev => ({ ...prev, [name]: formatPhoneNumber(value) }));
    } else if (name === 'zip_code') {
      setFormData(prev => ({ ...prev, [name]: formatZipCode(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name.trim()) {
      toast.error("First name is required");
      setActiveSection("contact");
      return;
    }
    if (!formData.last_name.trim()) {
      toast.error("Last name is required");
      setActiveSection("contact");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Email is required");
      setActiveSection("contact");
      return;
    }

    setSaving(true);
    try {
      let response;
      
      if (isEditMode) {
        response = await fetch(`${API}/admin/leads/${lead.lead_id}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });
      } else {
        response = await fetch(`${API}/admin/leads/create`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            ...formData,
            source: "outbound",
            stage: "Lead",
            status: "New"
          })
        });
      }

      if (response.ok) {
        toast.success(isEditMode ? "Lead updated successfully!" : "Lead created successfully!");
        onClose();
        if (onSuccess) onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to ${isEditMode ? 'update' : 'create'} lead`);
      }
    } catch (error) {
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} lead`);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" 
      onClick={onClose} 
      data-testid="lead-form-modal-overlay"
    >
      <div 
        className="bg-[#0a0a0a] border border-white/10 rounded-xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-form-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#0a0a0a]">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-[#14A800]/10">
              {isEditMode ? (
                <Edit className="w-5 h-5 text-[#14A800]" />
              ) : (
                <Target className="w-5 h-5 text-[#14A800]" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold text-white font-[Manrope]">
                {isEditMode ? "Edit Lead" : "Add New Lead"}
              </h2>
              <p className="text-xs text-zinc-500">
                {isEditMode ? "Update lead information" : "Create a new outbound lead"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/5 rounded-lg transition-colors"
            data-testid="lead-form-modal-close"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Section Tabs */}
        <div className="flex border-b border-white/10 bg-[#0a0a0a]">
          <button
            type="button"
            onClick={() => setActiveSection("company")}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeSection === "company" 
                ? "text-[#14A800] border-b-2 border-[#14A800] bg-[#14A800]/5" 
                : "text-zinc-400 hover:text-white hover:bg-white/5"
            }`}
            data-testid="company-section-tab"
          >
            <Building className="w-4 h-4" />
            Company Information
          </button>
          <button
            type="button"
            onClick={() => setActiveSection("contact")}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeSection === "contact" 
                ? "text-[#14A800] border-b-2 border-[#14A800] bg-[#14A800]/5" 
                : "text-zinc-400 hover:text-white hover:bg-white/5"
            }`}
            data-testid="contact-section-tab"
          >
            <User className="w-4 h-4" />
            Contact Information
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-4">
            
            {/* Company Information Section */}
            {activeSection === "company" && (
              <div className="space-y-4" data-testid="company-section">
                {/* Company Name */}
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Company Name</label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      placeholder="Acme Corporation"
                      className="w-full bg-[#121212] border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#14A800]/50"
                      data-testid="lead-company-name-input"
                    />
                  </div>
                </div>

                {/* Address Line 1 */}
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Address Line 1</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="text"
                      name="address_line_1"
                      value={formData.address_line_1}
                      onChange={handleChange}
                      placeholder="Street address or P.O. Box"
                      className="w-full bg-[#121212] border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#14A800]/50"
                      data-testid="lead-address-1-input"
                    />
                  </div>
                </div>

                {/* Address Line 2 */}
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Address Line 2</label>
                  <input
                    type="text"
                    name="address_line_2"
                    value={formData.address_line_2}
                    onChange={handleChange}
                    placeholder="Apartment, suite, unit, or building"
                    className="w-full bg-[#121212] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#14A800]/50"
                    data-testid="lead-address-2-input"
                  />
                </div>

                {/* City, State, ZIP Row */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1.5">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="San Diego"
                      className="w-full bg-[#121212] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#14A800]/50"
                      data-testid="lead-city-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1.5">State</label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="w-full bg-[#121212] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#14A800]/50"
                      data-testid="lead-state-select"
                    >
                      <option value="">Select state...</option>
                      {US_STATES.map(state => (
                        <option key={state.value} value={state.value}>{state.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1.5">ZIP Code</label>
                    <input
                      type="text"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      placeholder="92101"
                      maxLength={10}
                      className="w-full bg-[#121212] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#14A800]/50"
                      data-testid="lead-zip-input"
                    />
                  </div>
                </div>

                {/* Country */}
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Country</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full bg-[#121212] border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#14A800]/50"
                      data-testid="lead-country-select"
                    >
                      <option value="USA">United States of America</option>
                      <option value="CAN">Canada</option>
                      <option value="MEX">Mexico</option>
                      <option value="GBR">United Kingdom</option>
                      <option value="AUS">Australia</option>
                    </select>
                  </div>
                </div>

                {/* Company Phone & Email Row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1.5">Company Phone</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type="tel"
                        name="company_phone"
                        value={formData.company_phone}
                        onChange={handleChange}
                        placeholder="(619)555-0100"
                        maxLength={14}
                        className="w-full bg-[#121212] border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#14A800]/50"
                        data-testid="lead-company-phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1.5">Company Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type="email"
                        name="company_email"
                        value={formData.company_email}
                        onChange={handleChange}
                        placeholder="info@acme.com"
                        className="w-full bg-[#121212] border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#14A800]/50"
                        data-testid="lead-company-email-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Industry - Searchable */}
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Industry</label>
                  <SearchableSelect
                    options={INDUSTRIES}
                    value={formData.industry}
                    onChange={(val) => handleSelectChange("industry", val)}
                    placeholder="Select industry..."
                    icon={Factory}
                    testId="lead-industry-select"
                  />
                </div>
              </div>
            )}

            {/* Contact Information Section */}
            {activeSection === "contact" && (
              <div className="space-y-4" data-testid="contact-section">
                {/* First Name & Last Name Row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1.5">First Name <span className="text-red-400">*</span></label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        placeholder="John"
                        className="w-full bg-[#121212] border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#14A800]/50"
                        data-testid="lead-first-name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1.5">Last Name <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      placeholder="Doe"
                      className="w-full bg-[#121212] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#14A800]/50"
                      data-testid="lead-last-name-input"
                    />
                  </div>
                </div>

                {/* Job Title */}
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Job Title</label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="text"
                      name="job_title"
                      value={formData.job_title}
                      onChange={handleChange}
                      placeholder="Chief Executive Officer"
                      className="w-full bg-[#121212] border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#14A800]/50"
                      data-testid="lead-jobtitle-input"
                    />
                  </div>
                </div>

                {/* Mobile Phone */}
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Mobile Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="tel"
                      name="mobile_phone"
                      value={formData.mobile_phone}
                      onChange={handleChange}
                      placeholder="(619)555-0123"
                      maxLength={14}
                      className="w-full bg-[#121212] border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#14A800]/50"
                      data-testid="lead-mobile-input"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Email <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john.doe@acme.com"
                      className="w-full bg-[#121212] border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#14A800]/50"
                      data-testid="lead-email-input"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>

        {/* Footer Actions */}
        <div className="flex gap-3 p-4 border-t border-white/10 bg-[#0a0a0a]">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1 bg-white/5 border-white/10 text-white hover:bg-white/10 h-10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 bg-[#14A800] hover:bg-[#14A800]/90 text-black font-medium h-10"
            data-testid="save-lead-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isEditMode ? "Saving..." : "Creating..."}
              </>
            ) : (
              <>
                {isEditMode ? (
                  <>
                    <Edit className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                ) : (
                  <>
                    <Target className="w-4 h-4 mr-2" />
                    Create Lead
                  </>
                )}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LeadFormModal;
